import React, { useEffect, useState } from 'react'
import { getTranslation } from '../translations'
import axiosInstance from '../../../utils/axios';

function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-2">
      <div className="bg-[#134225] text-white font-extrabold px-3 py-2 uppercase tracking-wide text-sm text-center">{title}</div>
      {subtitle ? (
        <div className="text-[12px] italic text-[#334c3e] px-2 py-1 text-center">{subtitle}</div>
      ) : null}
    </div>
  )
}

function StageRow({ stage, practices, isFirst }) {
  return (
    <>
      {practices.map((practice, idx) => (
        <tr key={`${stage}-${idx}`} className="odd:bg-white even:bg-gray-50 text-sm">
          {idx === 0 && (
            <td 
              className="px-3 py-3 border border-[#e4ebe7] font-bold align-top" 
              rowSpan={practices.length}
            >
              {stage}
            </td>
          )}
          <td className="px-3 py-3 border border-[#e4ebe7]">{practice.practice}</td>
          <td className="px-3 py-3 border border-[#e4ebe7]">{practice.why}</td>
        </tr>
      ))}
    </>
  )
}

const SUPPORTED_CROPS = new Set([
  'maize',
  'mustard',
  'potato',
  'rice',
  'sugarcane',
  'wheat',
]);

export default function CropSpecificTips({
  cropName,
  language = 'english',
  title,
  subtitle,
  isPublic = false
}) {
  const t = getTranslation(language)
  const sectionTitle = title || t.section5Title
  const sectionSubtitle = subtitle || t.section5Subtitle
  
  const [tipsData, setTipsData] = useState(null)
  
  useEffect(() => {
    if (!cropName) {
      setTipsData(null);
      return;
    }

    const normalizedCrop = cropName.toLowerCase();

    if (!SUPPORTED_CROPS.has(normalizedCrop)) {
      setTipsData(null);
      return;
    }

    const fetchCropTips = async () => {
      try {
        const endpoint = isPublic
          ? `/public/crop-tips/${encodeURIComponent(normalizedCrop)}?lang=${language}`
          : `/api/v2/crop-tips/${encodeURIComponent(normalizedCrop)}?lang=${language}`;

        const { data: result } = await axiosInstance.get(endpoint);

        if (result.success && result.data?.tips) {
          setTipsData(result.data.tips);
        } else {
          setTipsData(null);
        }
      } catch (error) {
        console.error("Failed to fetch crop tips:", error);
        setTipsData(null);
      }
    };

    fetchCropTips();
  }, [cropName, language, isPublic]);

  const tipsArray = tipsData ? (tipsData[language.toLowerCase()] || tipsData.english) : null;
  const hasValidTips = Array.isArray(tipsArray) && tipsArray.length > 0;
  
  if (!tipsData || !hasValidTips) {
    return (
      <div className="mt-6">
        <SectionHeader title={sectionTitle} subtitle={sectionSubtitle} />
        <div className="border border-[#cbd9d2] rounded-md overflow-hidden bg-yellow-50">
          <div className="px-6 py-8 text-center">
            <div className="text-yellow-800 font-semibold text-lg mb-2">
              {t.cropTipsNotAvailable}
            </div>
            <p className="text-yellow-700 text-sm">
              {t.cropTipsNotAvailableDesc.replace('{crop}', cropName || 'Unknown')}
            </p>
          </div>
        </div>
      </div>
    )
  }
  
  const sortedStages = [...tipsArray].sort((a, b) => (a.stageOrder || 0) - (b.stageOrder || 0))
  
  return (
    <div className="mt-6">
      <SectionHeader title={sectionTitle} subtitle={sectionSubtitle} />
      <div className="border border-[#cbd9d2] rounded-md overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-[#134225] text-white text-sm">
              <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.stage}</th>
              <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.practice}</th>
              <th className="px-3 py-2 border border-[#d9e4dd] text-left">{t.why}</th>
            </tr>
          </thead>
          <tbody>
            {sortedStages.map((stage, i) => (
              <StageRow 
                key={stage.stageName} 
                stage={stage.stageName} 
                practices={stage.practices} 
                isFirst={i === 0} 
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
